import PropTypes from 'prop-types';
import React from 'react';
import { ButtonGroup, Button } from 'react-bootstrap';
import { cloneDeep, trimStart } from 'lodash';
import * as Table from 'reactabular-table';
import * as edit from 'react-edit';
import TwoElementsCell from './TwoElementsCell';
import ZoneHeader from './ZoneHeader';
import { each, map } from 'lodash/collection';
import RangeInput from './RangeInput';
import PriceInput from './PriceInput';
import Validate from './Validate';
import { DEFAULT_DISTANCE_UNIT } from 'utils/localization/types';


const epsilon = 0.00001;

const isFloatLessThan = (a, b) => (b - a) > ((Math.abs(a) < Math.abs(b) ? Math.abs(b) : Math.abs(a)) * epsilon);

const pricesMatrixToCsv = (tariffName, data) => {
    let csvContent = 'data:text/csv;charset=utf-8,';
    let dataString = 'Load Range,Weight Range,';
    each(data.zonesList, (zone, index) => {
        dataString += `Zone${index + 1} ${zone.minDistance}-${zone.maxDistance},`;
    });
    dataString += '\r\n';
    csvContent += dataString;
    each(data.rows, (row) => {
        dataString = `${row.loadRange.minPallets}-${row.loadRange.maxPallets},`;
        dataString += `${row.weightRange.minWeight}-${row.weightRange.maxWeight},`;
        each(row.pricesList, (price) => {
            dataString += `${price},`;
        });
        csvContent += `${dataString}\r\n`;
    });
    const filename = `${tariffName}.csv`;
    const encodedUri = encodeURI(csvContent);
    const link = (<div><a
      href={encodedUri}
      download={filename}
      target="_blank"
    >Export to CSV</a></div>);
    return link;
};

const dataWithTariff = (tariffRate) => {
    const data = {};
    data.zonesList = map(tariffRate.distance_ranges, function (zone, index) {
        return {
            minDistance: zone[0],
            maxDistance: zone[1],
        };
    });

    data.rows = [];
    const count = (tariffRate.weight_ranges) ? tariffRate.weight_ranges.length : 0;
    for (let i = 0; i < count; ++i) {
        const pricesList = map(tariffRate.prices, function (item) {
            if (i < item.length) {
                return item[i];
            }
        });
        let minWeight = 0;
        let maxWeight = 0;
        if (i < tariffRate.weight_ranges.length) {
            minWeight = tariffRate.weight_ranges[i][0];
            maxWeight = tariffRate.weight_ranges[i][1];
        }
        const row = {
            loadRange: {
                minPallets: tariffRate.load_ranges[i][0],
                maxPallets: tariffRate.load_ranges[i][1],
            },
            weightRange: {
                minWeight: minWeight,
                maxWeight: maxWeight,
            },
            pricesList: pricesList,
        };
        data.rows.push(row);
    }
    return data;
};

const loadRangesFromData = data => map(data.rows, (row) => {
    const loadRange = row.loadRange;
    return [loadRange.minPallets, loadRange.maxPallets];
});

const weightRangesFromData = data => map(data.rows, (row) => {
    const weightRange = row.weightRange;
    return [weightRange.minWeight, weightRange.maxWeight];
});

const zonesFromData = data => map(data.zonesList, zone => [zone.minDistance, zone.maxDistance]);

const zoneToLoadFromData = (data) => {
    const prices = [];
    each(data.rows, (row, rowIndex) => {
        each(row.pricesList, (price, priceIndex) => {
            if (!prices[priceIndex]) {
                prices[priceIndex] = [];
            }
            prices[priceIndex][rowIndex] = price;
        });
    });
    return prices;
};


class PalletBaseTable extends React.Component {
    static propTypes = {
        tariffRate: PropTypes.object,
        onChange: PropTypes.func,
    };

    constructor(props) {
        super(props);
        const formatters = {
            fixedFloat: value => parseFloat(value).toFixed(2),
            fixedInt: value => parseInt(value),
        };
        const validators = {
            prices: (value, { property, rowIndex }) => Validate(value => (value > 0), (value) => {
                const row = this.state.data.rows[rowIndex];
                const priceIndex = parseInt(trimStart(property, 'Price')) - 1;
                if (priceIndex) {
                    const prevRowPrice = row.pricesList[priceIndex - 1];
                    if (isFloatLessThan(value, prevRowPrice)) {
                        return true;
                    }
                }
                if (rowIndex) {
                    const prevRow = this.state.data.rows[rowIndex - 1];
                    const prevColPrice = prevRow.pricesList[priceIndex];
                    if (isFloatLessThan(value, prevColPrice)) {
                        return true;
                    }
                }
                return false;
            })(value),
            zones: (zone, index) => Validate((value) => {
                const minDistance = zone.minDistance;
                const maxDistance = zone.maxDistance;
                if (value === maxDistance) {
                    return (value > 0 && value >= minDistance);
                }
                return (value >= 0 || !index);
            }),
        };

        this.state = {
            tariffRate: {},
            formatters: formatters,
            data: { rows: [] },
            editedCell: null,
            editedKey: null,
            validators: validators,
        };
    }
    componentDidMount() {
        const tariffRate = this.props.tariffRate;
        let data = this.state.data;
        if (tariffRate) {
            data = dataWithTariff(tariffRate);
            this.setState({ data: data });
        }
    };

    createRow = (index) => {
        const dataRows = this.state.data.rows || [];
        let newRow = {};
        if (dataRows.length && index > 0 && index <= dataRows.length) {
            const lastRow = dataRows[index - 1];
            newRow = JSON.parse(JSON.stringify(lastRow));
        } else {
            newRow = {
                loadRange: { minPallets: 0, maxPallets: 0 },
                weightRange: { minWeight: 0, maxWeight: 0 },
            };
        }
        return newRow;
    };

    addRow = () => {
        const data = this.state.data;
        const dataRows = data.rows || [];
        const index = dataRows.length;
        const newRow = this.createRow(index);
        dataRows.push(newRow);
        data.rows = dataRows;
        this.setState({ data: data });
        this.onChangePrices({
            load_ranges: loadRangesFromData(data),
            weight_ranges: weightRangesFromData(data),
            prices: zoneToLoadFromData(data),
        });
    };

    insertRow = (index) => {
        const data = this.state.data;
        const dataRows = data.rows || [];
        const newRow = this.createRow(index);
        if (dataRows.length && index <= dataRows.length) {
            dataRows.splice(index, 0, newRow);
            data.rows = dataRows;
            this.setState({ data: data });
            this.onChangePrices({
                load_ranges: loadRangesFromData(data),
                weight_ranges: weightRangesFromData(data),
                prices: zoneToLoadFromData(data),
            });
        }
    };

    removeRow = (index) => {
        const data = this.state.data;
        const dataRows = data.rows || [];
        if (dataRows.length && index < dataRows.length) {
            dataRows.splice(index, 1);
            data.rows = dataRows;
            this.setState({ data: data });
            this.onChangePrices({
                load_ranges: loadRangesFromData(data),
                weight_ranges: weightRangesFromData(data),
                prices: zoneToLoadFromData(data),
            });
        }
    };

    addZone = () => {
        const data = this.state.data;
        if (!data.zonesList) {
            data.zonesList = [];
        }
        data.zonesList.push({
            minDistance: 0,
            maxDistance: 0,
        });
        each(data.rows, function (item) {
            if (!item.pricesList) {
                item.pricesList = [];
            }
            item.pricesList.push(0);
        });
        this.setState({ data: data });
        this.onChangePrices({
            distance_ranges: zonesFromData(data),
            prices: zoneToLoadFromData(data),
        });
    };

    removeZone = (index) => {
        const data = this.state.data;
        data.zonesList.splice(index, 1);
        each(data.rows, function (item) {
            if (item.pricesList) {
                item.pricesList.splice(index, 1);
            }
        });
        this.setState({ data: data });
        this.onChangePrices({
            distance_ranges: zonesFromData(data),
            prices: zoneToLoadFromData(data),
        });
    };

    onChangePrices = (changedProperty) => {
        if (this.props.onChange) {
            this.props.onChange(changedProperty);
        }
    };

    tableData = () => {
        const data = this.state.data;
        const tableData = map(data.rows, function (item) {
            const row = {
                loadRange: item.loadRange,
                weightRange: item.weightRange,
                editing: item.editing,
            };
            each(item.pricesList, function (priceItem, index) {
                const priceKey = `Price${index + 1}`;
                row[priceKey] = priceItem;
            });
            return row;
        });
        return tableData;
    };

    tableColumns = () => {
        const formatters = (this.state) ? this.state.formatters : {};
        const data = this.state.data;
        const validators = this.state.validators;
        const editableRange = edit.edit({
            isEditing: ({ columnIndex, rowData }) => columnIndex === rowData.editing,
            onActivate: ({ columnIndex, rowIndex }) => {
            },
            onValue: ({ value, rowIndex, property }) => {
                const dataClone = cloneDeep(this.state.data);
                const row = dataClone.rows[rowIndex];
                row[property] = value;
                row.editing = false;
                this.setState({ data: dataClone, editedKey: null });
                if (property === 'loadRange') {
                    this.onChangePrices({ load_ranges: loadRangesFromData(dataClone) });
                } else if (property === 'weightRange') {
                    this.onChangePrices({ weight_ranges: weightRangesFromData(dataClone) });
                }
            },
        });
        const weightRangeCell = TwoElementsCell.bind(this, 'minWeight', 'maxWeight');
        const loadRangeCell = TwoElementsCell.bind(this, 'minPallets', 'maxPallets');
        const columns = [];
        if (this.props.tariffRate.type === 'palletBase') {
            columns.push({
                property: 'loadRange',
                header: {
                    label: 'Load Range, pallets',
                },
                cell: {
                    formatters: [
                        loadRangeCell(),
                    ],
                    transforms: [
                        editableRange(RangeInput.bind(this, 'minPallets', 'maxPallets')()),
                    ],
                },
            });
        }
        columns.push({
            property: 'weightRange',
            header: {
                label: 'Weight Range, lbs',
            },
            cell: {
                formatters: [
                    weightRangeCell(),
                ],
                transforms: [
                    editableRange(RangeInput.bind(this, 'minWeight', 'maxWeight')()),
                ],
            },
        });

        if (data.rows) {
            each(data.zonesList, (zone, index) => {
                const property = `Zone ${index + 1}, ${DEFAULT_DISTANCE_UNIT}`;
                let headerEditor = ZoneHeader.bind(this, zone, 'editedCell', property, (value, key) => {
                    zone[key] = parseInt(value);
                    this.setState({ editedKey: null });
                    this.onChangePrices({ distance_ranges: zonesFromData(data) });
                });
                const columnProperty = `Price${index + 1}`;
                headerEditor = headerEditor({ validator: validators.zones(zone, index) });
                const removeZone = () => {
                    this.removeZone(index);
                };

                const columnHeader = (<div>
                  <div>{`${property} `}
                    <Button
                      bsSize="xsmall"
                      className="btn btn-danger"
                      onClick={removeZone}
                    >&#10006;
                        </Button>
                  </div>
                  <div {...headerEditor.props}>{headerEditor.value}
                  </div>
                </div>);
                const editable = edit.edit({
                    isEditing: ({ columnIndex, rowData }) => columnIndex === rowData.editing,
                    onActivate: ({ columnIndex, rowIndex }) => {
                        const dataClone = cloneDeep(this.state.data);
                        dataClone.rows[rowIndex].editing = columnIndex;
                        this.setState({ data: dataClone });
                    },
                    onValue: ({ value, rowIndex, property }) => {
                        const dataClone = cloneDeep(this.state.data);
                        const row = dataClone.rows[rowIndex];
                        const priceIndex = parseInt(trimStart(property, 'Price')) - 1;
                        const updatedValue = parseFloat(value);
                        row.pricesList[priceIndex] = updatedValue;
                        row.editing = false;
                        this.setState({ data: dataClone, editedKey: null });
                        this.onChangePrices({ prices: zoneToLoadFromData(dataClone) });
                    },
                });

                columns.push({
                    property: columnProperty,
                    header: {
                        label: columnHeader,
                    },
                    cell: {
                        formatters: [
                            formatters.fixedFloat,
                            validators.prices,

                        ],
                        transforms: [
                            editable(PriceInput()),
                        ],
                    },
                    zoneColumn: true,
                });
            });

            columns.push({
                header: {
                    formatters: [
                        () => (<Button
                          bsSize="xsmall"
                          className="btn btn-default"
                          onClick={this.addZone}
                          disabled={data.rows.length <= 0}
                        >Add Zone</Button>),
                    ],
                },
                cell: {
                    formatters: [
                        (value, { rowData, rowIndex }) => {
                            const remove = () => {
                                this.removeRow(rowIndex);
                            };
                            const insert = () => {
                                const nextIndex = rowIndex + 1;
                                this.insertRow(nextIndex);
                            };

                            return (
                              <div>
                                <ButtonGroup bsSize="xsmall">
                                  <Button
                                    className="btn btn-success"
                                    onClick={insert}
                                  >&#10010;
                                        </Button>
                                  <Button
                                    className="btn btn-danger"
                                    onClick={remove}
                                  >&#10006;
                                        </Button>
                                </ButtonGroup>
                              </div>
                            );
                        },
                    ],
                },
            });
        }
        return columns;
    };

    render() {
        const columns = this.tableColumns();
        const tableData = this.tableData();
        return (
          <Table.Provider
            className="default-table"
            columns={columns}
          >
            <Table.Header />
            <Table.Body rows={tableData} rowKey={({ rowIndex }) => `${rowIndex}price-table`} />
            <tfoot>
              <tr>
                {columns.map((column, i) => {
                    if (i === 0) {
                          return (<td key={`${i}-footer`}>
                              <Button
                                  bsSize="xsmall"
                                  className="btn btn-default"
                                  onClick={this.addRow}
                                >Add Range Row
                                                </Button>
                            </td>);
                      } else if (i === (columns.length - 1)) {
                            const tariffRate = this.props.tariffRate;
                            const link = pricesMatrixToCsv(tariffRate.name, this.state.data);
                            return (<td key={`${i}-footer`}>{link}</td>);
                        }
                    return (<td key={`${i}-footer`} />);
                })}
              </tr>
            </tfoot>
          </Table.Provider >

        );
    }
}

export default PalletBaseTable;
